import Component from '@glimmer/component';
import { service } from '@ember/service';
import { Section } from 'tio-ui/components/layout';
import ProgressBar from 'tio-reimagined/components/progress-bar';
import { PhClockCountdown, PhNotepad } from 'ember-phosphor-icons';
import { t } from 'ember-intl';
import { on } from '@ember/modifier';
import type FeaturesService from 'tio-common/services/features';
import type AccountModel from 'tio-common/models/account';

interface LoanProgress {
  id: string;
  name: string;
  qualifyingPayments: number;
  totalRequired: number;
  percentage: number;
  hasProgress: boolean;
}

interface QualifyingPaymentTrackerSignature {
  Args: {
    accounts?: AccountModel[];
    onAddFile?: () => void;
  };
  Element: HTMLDivElement;
}

class QualifyingPaymentTracker extends Component<QualifyingPaymentTrackerSignature> {
  @service declare features: FeaturesService;

  readonly TOTAL_REQUIRED_PAYMENTS = 120;

  get isEnabled() {
    return this.features.isEnabled('pslfQualifyingPaymentTracker');
  }

  get loansWithProgress(): LoanProgress[] {
    const accounts = this.args.accounts ?? [];
    const loans: LoanProgress[] = [];

    for (const account of accounts) {
      if (!account.isActive) continue;

      for (const loan of account.activeLoans ?? []) {
        const isPaidOff = loan.observation?.['is-paid-off'] ?? false;
        const qualified = loan.observation?.['forgiveness-program-qualified'] ?? 0;
        if (isPaidOff && qualified === 0) continue;
        loans.push({
          id: loan.id ?? '',
          name: loan.name ?? `Loan ${loan.id ?? ''}`,
          qualifyingPayments: qualified,
          totalRequired: this.TOTAL_REQUIRED_PAYMENTS,
          percentage: Math.min(Math.round((qualified / this.TOTAL_REQUIRED_PAYMENTS) * 100), 100),
          hasProgress: qualified > 0,
        });
      }
    }

    // Sort by most progress first
    loans.sort((a, b) => b.qualifyingPayments - a.qualifyingPayments);

    return loans;
  }

  get hasLoans() {
    return this.loansWithProgress.length > 0;
  }

  <template>
    {{#if this.isEnabled}}
      {{#if this.hasLoans}}
        <Section data-test-qualifying-payment-tracker>
          <:header>
            <span class="flex items-center gap-2">
              <PhNotepad @size={{20}} @weight="fill" />
              {{t "pslf.payment_tracker.title"}}
            </span>
          </:header>
          <:body>
            <div class="divide-y divide-gray-100">
              {{#each this.loansWithProgress as |loan|}}
                <div class="py-3 first:pt-0 last:pb-0">
                  <div class="flex justify-between items-center mb-1">
                    <span class="font-semibold text-sm text-gray-900">
                      {{loan.name}}
                    </span>
                    {{#if loan.hasProgress}}
                      <span class="text-sm text-gray-600">
                        {{loan.qualifyingPayments}}/{{loan.totalRequired}}
                        {{t "pslf.payment_tracker.payments"}}
                      </span>
                    {{else}}
                      <span class="flex items-center gap-1 text-sm text-gray-500">
                        <PhClockCountdown @size={{16}} />
                        {{t "pslf.payment_tracker.no_progress"}}
                      </span>
                    {{/if}}
                  </div>
                  <ProgressBar
                    @value={{loan.percentage}}
                    @color="violet"
                    @size="md"
                    @label={{loan.name}}
                  />
                </div>
              {{/each}}
            </div>

            <p class="text-sm text-gray-500 mt-4">
              {{t "pslf.payment_tracker.not_right"}}
              {{#if @onAddFile}}
                <button
                  type="button"
                  class="text-ocean-600 underline hover:text-ocean-700"
                  data-test-pslf-add-file-button
                  {{on "click" @onAddFile}}
                >
                  {{t "pslf.payment_tracker.add_file"}}
                </button>
              {{/if}}
            </p>
          </:body>
        </Section>
      {{/if}}
    {{/if}}
  </template>
}

export default QualifyingPaymentTracker;
